import { useRef, type TouchEvent } from 'react'
import { chatv1SelfUser } from '../chatv1/auth'
import { Icon } from '../ui/Icon'
import { useBoards } from './BoardMenu'
import { lastBoard } from './boardNav'
import { isNews, newestNews } from './boardSeen'
import './board.css'

export type RoomTool = 'board' | 'pages'

/**
 * A PROJECT ROOM'S COLUMN OF TOOLS — owner, 2026-11-02: 幫我將 白板, Pages (筆記本). 都放在 Bookmark 的上方作為一列的按鈕.
 * (比放在 header 更好). 然後手機窄版頁面也用抽屜打開的方式 … a column of buttons on the right hand side is better.
 *
 * Over the room's Bookmarks button, in the column at the right: the Whiteboard, then the Pages — corner buttons on a wide
 * screen, pull-out handles at the right edge on a phone, each opening its drawer (RoomBoardDrawer, PagesDrawer). Where
 * the room has no bookmarks the whiteboard takes their place. The whiteboard wears a dot while a board of the room has
 * changed since this person looked (boardSeen.ts), and then opens on that board; else on the one this device had open.
 */
export function RoomCorner({
  convId,
  pages,
  low,
  handles,
  onOpen,
}: {
  convId: string
  /** The build offers pages (pages.ts pagesOn). */
  pages: boolean
  /** No Bookmarks button under them: the first place in the column is the whiteboard's. */
  low: boolean
  /** The phone's handles show (not while another drawer is out over them). */
  handles: boolean
  onOpen: (tool: RoomTool, board?: string) => void
}) {
  const { boards } = useBoards(convId)
  const me = chatv1SelfUser()
  const news = (boards ?? []).filter((b) => isNews(b, me))
  const target = newestNews(boards ?? [], me)
  const said = news.length === 1 ? `${news[0]!.name} has changed` : `${news.length} boards have changed`
  const openBoard = () => onOpen('board', target?.board ?? lastBoard(convId))
  const openPages = () => onOpen('pages')
  const wbSlot = low ? 0 : 1
  const pgSlot = wbSlot + 1
  const wbTip = news.length ? `Whiteboard — ${said}` : 'Whiteboard'

  // A handle pulled from right to left opens its drawer, as the notes' and the bookmarks' do.
  const x = useRef(0)
  const pull = (open: () => void) => ({
    onTouchStart: (e: TouchEvent) => {
      x.current = e.touches[0]?.clientX ?? 0
    },
    onTouchMove: (e: TouchEvent) => {
      const now = e.touches[0]?.clientX ?? 0
      if (x.current - now > 8) {
        x.current = Number.NEGATIVE_INFINITY // once per gesture
        open()
      }
    },
  })

  return (
    <>
      <button
        type="button"
        id="roomWbFab"
        className={`corner-fab room-tool slot-${wbSlot}`}
        data-tip={wbTip}
        aria-label={news.length ? `Open the whiteboard — ${said}` : 'Open the whiteboard'}
        onClick={openBoard}
      >
        <Icon name="schema" size={17} />
        {news.length > 0 && <span className="cf-dot" />}
      </button>
      {pages && (
        <button type="button" id="roomPgFab" className={`corner-fab room-tool slot-${pgSlot}`} data-tip="Pages" aria-label="Open the pages" onClick={openPages}>
          <Icon name="description" size={17} />
        </button>
      )}
      {handles && (
        <>
          <button
            type="button"
            id="roomWbHandle"
            className={[`room-handle slot-${wbSlot}`, news.length ? 'has' : ''].filter(Boolean).join(' ')}
            aria-label={news.length ? `Whiteboard, ${said} — pull to open` : 'Whiteboard — pull to open'}
            title="Pull out the whiteboard"
            onClick={openBoard}
            {...pull(openBoard)}
          >
            <Icon name="schema" size={11} />
          </button>
          {pages && (
            <button
              type="button"
              id="roomPgHandle"
              className={`room-handle slot-${pgSlot}`}
              aria-label="Pages — pull to open"
              title="Pull out the pages"
              onClick={openPages}
              {...pull(openPages)}
            >
              <Icon name="description" size={11} />
            </button>
          )}
        </>
      )}
    </>
  )
}
